import { type FC } from "react";
import { Button } from "antd";
import leftIcon from "../../../assets/images/left-arr.svg";
import rightGrayIcon from "../../../assets/images/right-arr-gray.svg";
import rightWhiteIcon from "../../../assets/images/right-arr-white.svg";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import { useFeatureFlags } from "../../../context/LDFlagsContext";
import { useTrialsRouter } from "../../../shared/custom-hooks/useTrialsRouter";
import { appURLs } from "../../../lib/appRoutes/appUrls";

const PLOT_DESIGN_STEP = 1;

export interface FormNavigationButtonsProps {
  current: number;
  cancelLabel: string;
  nextLabel: string;
  isStepValid: boolean;
  isNextDisabled: boolean;
  onCancelClick: () => void;
  onNextClick: () => void;
  // Persists Plot Design before opening the standalone Rates & Dosage route;
  // resolving false keeps the user on Plot Design.
  onSavePlotDesign?: () => Promise<boolean>;
}

const FormNavigationButtons: FC<FormNavigationButtonsProps> = ({
  current,
  cancelLabel,
  nextLabel,
  isStepValid,
  isNextDisabled,
  onCancelClick,
  onNextClick,
  onSavePlotDesign,
}) => {
  const { t } = useTranslation();
  const { enableAutoProductAssignment } = useFeatureFlags();
  const { navigate, workspaceId, orgId, farmId, trialId } = useTrialsRouter();

  // The standalone route has its own trial state and reloads the trial from
  // the API, so unsaved plots would not reach it.
  const goToNewRatesAndDosage = async (): Promise<void> => {
    if (onSavePlotDesign && !(await onSavePlotDesign())) {
      return;
    }
    navigate(
      `${appURLs.TRIALS}/workspace/${workspaceId as string}/org/${
        orgId as string
      }/farm/${farmId as string}/trial/${
        trialId as string
      }/new-rates-and-dosage`
    );
  };

  return (
    <div className="bottom-sec-btn">
      <Button
        className="cancel-btn"
        data-testid="cancel-btn"
        onClick={onCancelClick}
      >
        <img src={leftIcon} className="left-icon" alt="left-icon" />
        {cancelLabel}
      </Button>

      {current === PLOT_DESIGN_STEP && enableAutoProductAssignment && (
        <Button
          data-testid="new-rates-dosage-btn"
          disabled={isNextDisabled}
          onClick={() => {
            void goToNewRatesAndDosage();
          }}
        >
          {formatTranslation(t("newTrial.plotDesign.newRatesDosageButton"))}
        </Button>
      )}

      <Button
        className={`next-btn ${isStepValid ? "" : "active"}`}
        htmlType="button"
        data-testid="next-btn"
        disabled={isNextDisabled}
        onClick={onNextClick}
      >
        {nextLabel}
        {nextLabel !== "Finish" && (
          <img
            src={isStepValid ? rightWhiteIcon : rightGrayIcon}
            alt="left-icon"
          />
        )}
      </Button>
    </div>
  );
};

export default FormNavigationButtons;
